import { useMemo } from 'react';
import type { CalendarEvent } from '../page';

interface CalendarGridProps {
  events: CalendarEvent[];
  viewMode: 'month' | 'week' | 'year';
  currentDate: Date;
  onEventClick: (event: CalendarEvent) => void;
  levelColors: Record<string, string>;
}

export default function CalendarGrid({ events, viewMode, currentDate, onEventClick, levelColors }: CalendarGridProps) {
  // Get calendar days for month view
  const monthDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days in month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  }, [currentDate]);

  // Get week days
  const weekDays = useMemo(() => {
    const days: Date[] = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    
    return days;
  }, [currentDate]);

  // Get year months
  const yearMonths = useMemo(() => {
    const months: Date[] = [];
    const year = currentDate.getFullYear();
    
    for (let month = 0; month < 12; month++) {
      months.push(new Date(year, month, 1));
    }
    
    return months;
  }, [currentDate]);

  // Get events for a specific date
  const getEventsForDate = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    
    return events.filter(event => {
      const start = new Date(event.startDate);
      const end = new Date(event.endDate);
      
      // Normalize dates to compare only year, month, and day
      const startYear = start.getFullYear();
      const startMonth = start.getMonth();
      const startDay = start.getDate();
      
      const endYear = end.getFullYear();
      const endMonth = end.getMonth();
      const endDay = end.getDate();
      
      // Check if the current date falls within the event date range
      const currentDateNum = year * 10000 + month * 100 + day;
      const startDateNum = startYear * 10000 + startMonth * 100 + startDay;
      const endDateNum = endYear * 10000 + endMonth * 100 + endDay;
      
      return currentDateNum >= startDateNum && currentDateNum <= endDateNum;
    }).sort((a, b) => {
      const levelPriority = { GC: 0, Division: 1, Union: 2, National: 3 };
      return levelPriority[a.level] - levelPriority[b.level];
    });
  };

  // Get events for a month (year view)
  const getEventsForMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return events.filter(event => {
      const start = new Date(event.startDate);
      const end = new Date(event.endDate);
      return (start.getFullYear() === year && start.getMonth() === month) ||
             (end.getFullYear() === year && end.getMonth() === month) ||
             (start < new Date(year, month, 1) && end > new Date(year, month + 1, 0));
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  if (viewMode === 'month') {
    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-3 text-center text-sm font-semibold text-gray-700">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {monthDays.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="min-h-32 bg-gray-50 border-b border-r border-gray-200"></div>;
            }

            const dayEvents = getEventsForDate(date);
            const today = isToday(date);

            return (
              <div
                key={index}
                className={`min-h-32 border-b border-r border-gray-200 p-2 ${
                  today ? 'bg-emerald-50' : 'bg-white hover:bg-gray-50'
                } transition-colors`}
              >
                <div className={`text-sm font-semibold mb-2 ${
                  today ? 'text-emerald-600' : 'text-gray-900'
                }`}>
                  {date.getDate()}
                  {today && <span className="ml-1 text-xs">(Today)</span>}
                </div>

                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map(event => (
                    <button
                      key={event.id}
                      onClick={() => onEventClick(event)}
                      className={`w-full text-left px-2 py-1 rounded text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${
                        event.level === 'GC' ? 'bg-slate-800 text-white' :
                        event.level === 'Division' ? 'bg-blue-600 text-white' :
                        event.level === 'Union' ? 'bg-amber-600 text-white' :
                        'bg-emerald-600 text-white'
                      }`}
                    >
                      <div className="truncate">{event.title}</div>
                    </button>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-xs text-gray-600 px-2">
                      +{dayEvents.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (viewMode === 'week') {
    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {weekDays.map(date => {
            const today = isToday(date);
            return (
              <div key={date.toISOString()} className={`p-3 text-center ${today ? 'bg-emerald-100' : ''}`}>
                <div className="text-xs text-gray-600">
                  {date.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div className={`text-lg font-bold ${today ? 'text-emerald-600' : 'text-gray-900'}`}>
                  {date.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Week Grid */}
        <div className="grid grid-cols-7">
          {weekDays.map(date => {
            const dayEvents = getEventsForDate(date);
            const today = isToday(date);

            return (
              <div
                key={date.toISOString()}
                className={`min-h-96 border-r border-gray-200 p-3 ${
                  today ? 'bg-emerald-50' : 'bg-white'
                }`}
              >
                <div className="space-y-2">
                  {dayEvents.map(event => (
                    <button
                      key={event.id}
                      onClick={() => onEventClick(event)}
                      className={`w-full text-left p-3 rounded-lg cursor-pointer hover:shadow-md transition-all ${
                        event.level === 'GC' ? 'bg-slate-100 border-2 border-slate-300' :
                        event.level === 'Division' ? 'bg-blue-100 border-2 border-blue-300' :
                        event.level === 'Union' ? 'bg-amber-100 border-2 border-amber-300' :
                        'bg-emerald-100 border-2 border-emerald-300'
                      }`}
                    >
                      <div className={`text-xs font-semibold mb-1 ${
                        event.level === 'GC' ? 'text-slate-800' :
                        event.level === 'Division' ? 'text-blue-800' :
                        event.level === 'Union' ? 'text-amber-800' :
                        'text-emerald-800'
                      }`}>
                        {event.levelName}
                      </div>
                      <div className="text-sm font-bold text-gray-900 mb-1">{event.title}</div>
                      {event.time && (
                        <div className="text-xs text-gray-600">
                          <i className="ri-time-line mr-1"></i>
                          {event.time}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Year view
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {yearMonths.map(monthDate => {
        const monthEvents = getEventsForMonth(monthDate);
        const isCurrentMonthView = isCurrentMonth(monthDate);

        return (
          <div
            key={monthDate.toISOString()}
            className={`bg-white rounded-xl shadow-md p-4 transition-all ${
              isCurrentMonthView ? 'ring-4 ring-emerald-500 shadow-xl scale-105' : ''
            }`}
          >
            <h3 className={`text-lg font-bold mb-3 ${
              isCurrentMonthView ? 'text-emerald-600' : 'text-gray-900'
            }`} style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
              {monthDate.toLocaleDateString('en-US', { month: 'long' })}
              {isCurrentMonthView && (
                <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                  Current Month
                </span>
              )}
            </h3>

            <div className="space-y-2">
              {monthEvents.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No events</p>
              ) : (
                <>
                  {monthEvents.slice(0, 5).map(event => (
                    <button
                      key={event.id}
                      onClick={() => onEventClick(event)}
                      className={`w-full text-left p-2 rounded-lg text-xs cursor-pointer hover:shadow-md transition-all ${
                        event.level === 'GC' ? 'bg-slate-100 border border-slate-300' :
                        event.level === 'Division' ? 'bg-blue-100 border border-blue-300' :
                        event.level === 'Union' ? 'bg-amber-100 border border-amber-300' :
                        'bg-emerald-100 border border-emerald-300'
                      }`}
                    >
                      <div className={`font-semibold ${
                        event.level === 'GC' ? 'text-slate-800' :
                        event.level === 'Division' ? 'text-blue-800' :
                        event.level === 'Union' ? 'text-amber-800' :
                        'text-emerald-800'
                      }`}>
                        {event.title}
                      </div>
                      <div className="text-gray-600 mt-1">
                        {new Date(event.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </button>
                  ))}
                  {monthEvents.length > 5 && (
                    <div className="text-xs text-gray-600 px-2 py-1">
                      +{monthEvents.length - 5} more events
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
